import { Grid2 as Grid, Typography } from "@mui/material";
import React from "react";
import { InvoiceDetailsModel } from "../../models/ChasableModels";
import { TableSection } from "../../components/TableSection";
import { ExternalLink } from "../../components/ExternalLink";
import { getSeverityColor } from "../../services/helpers";
import { EditPayDateButton } from "./EditPayDateButton";
import { formatDate } from "../../utils/formatDate";

type Props = {
  invoice: InvoiceDetailsModel;
  companyName: string;
  supplierLink?: string;
  accountNumber?: string;
};

export function InvoiceInformation({ invoice, companyName, supplierLink, accountNumber }: Props) {
  const tableDefinition = [
    {
      title: "Type",
      value: "Purchase invoice",
    },
    {
      title: "Amount",
      value: Intl.NumberFormat(Office.context.displayLanguage, {
        currency: invoice.currency ?? "GBP",
        style: "currency",
      }).format(invoice.amount),
    },
    {
      title: "Account Number",
      value: accountNumber,
    },
    {
      title: "Supplier",
      value: <ExternalLink href={supplierLink}>{companyName}</ExternalLink>,
    },
    {
      title: "Invoice",
      value: <ExternalLink href={invoice.link}>{invoice.invoiceNumber}</ExternalLink>,
    },
    {
      title: "Date",
      value: formatDate(invoice.invoiceDate),
    },
    {
      title: "Due Date",
      value: formatDate(invoice.dueDate),
    },
    {
      title: "Planned Date",
      value: <PlannedDate date={invoice.plannedDate} />,
    },
    {
      title: "Status",
      value: <Status invoice={invoice} />,
    },
  ];

  return <TableSection title="Information" ariaLabel="Invoice Table" tableDefinition={tableDefinition} />;
}

function Status({ invoice }: { invoice: InvoiceDetailsModel }) {
  const { amount, remainingAmount } = invoice;

  switch (true) {
    case amount === remainingAmount:
      return (
        <Typography fontWeight={700} color={getSeverityColor("error")}>
          Unpaid
        </Typography>
      );
    case amount > remainingAmount && remainingAmount !== 0:
      return (
        <Typography fontWeight={700} color={getSeverityColor("warning")}>
          Partially paid
        </Typography>
      );
    case remainingAmount === 0:
      return (
        <Typography fontWeight={700} color={getSeverityColor("success")}>
          Paid
        </Typography>
      );
    default:
      return null;
  }
}

function PlannedDate({ date }: { date?: string | null }) {
  return (
    <Grid container={true} alignItems="center" justifyContent="end" flexWrap="nowrap">
      <Grid mr={0.5}>{date ? formatDate(date) : "N/A"}</Grid>
      <EditPayDateButton date={date} />
    </Grid>
  );
}
